<template>
  <Transition name="fade">
    <div
      v-if="show"
      class="fixed inset-0 bg-slate-900/60 backdrop-blur-md z-[100] flex items-center justify-center p-6"
      @click.self="$emit('close')"
    >
      <div class="bg-white rounded-[2.5rem] p-10 max-w-3xl w-full shadow-2xl scale-up max-h-[80vh] overflow-y-auto">
        <!-- Header -->
        <div class="flex justify-between items-center mb-8">
          <h2 class="text-2xl font-extrabold text-slate-900">Gérer les langues</h2>
          <button @click="$emit('close')" class="text-2xl text-slate-300 hover:text-slate-500">✕</button>
        </div>

        <!-- Current Languages -->
        <div class="mb-8">
          <h3 class="text-sm font-bold text-slate-700 mb-4">Langues actives</h3>
          <div class="space-y-2">
            <div
              v-for="(lang, idx) in enabledLanguages"
              :key="lang.code"
              class="flex items-center justify-between p-4 bg-slate-50 rounded-xl"
            >
              <div class="flex items-center gap-3">
                <span class="text-2xl">{{ lang.flag }}</span>
                <div>
                  <div class="font-semibold text-slate-800">{{ lang.label }}</div>
                  <div class="text-xs text-slate-500">{{ lang.code }}</div>
                </div>
                <span v-if="lang.custom" class="px-2 py-1 bg-indigo-100 text-indigo-700 text-xs rounded-full font-semibold">
                  Personnalisée
                </span>
              </div>
              <button
                @click="removeLanguage(idx)"
                class="text-red-500 hover:text-red-700 p-2"
                :disabled="enabledLanguages.length === 1"
                :class="{ 'opacity-30 cursor-not-allowed': enabledLanguages.length === 1 }"
              >
                🗑️
              </button>
            </div>
          </div>
        </div>

        <!-- Predefined Languages -->
        <div class="mb-8">
          <h3 class="text-sm font-bold text-slate-700 mb-4">Langues prédéfinies</h3>
          <div class="grid grid-cols-3 gap-3">
            <button
              v-for="lang in availablePredefinedLanguages"
              :key="lang.code"
              @click="addPredefinedLanguage(lang)"
              class="flex items-center gap-2 p-3 rounded-xl border-2 border-slate-100 hover:border-indigo-500 hover:bg-indigo-50 transition-all"
            >
              <span class="text-xl">{{ lang.flag }}</span>
              <span class="text-sm font-semibold text-slate-700">{{ lang.label }}</span>
            </button>
          </div>
        </div>

        <!-- Add Custom Language -->
        <div class="pt-6 border-t border-slate-200">
          <h3 class="text-sm font-bold text-slate-700 mb-4">Ajouter une langue personnalisée</h3>
          <div class="space-y-4">
            <div class="grid grid-cols-3 gap-3">
              <div>
                <label class="text-xs text-slate-500 mb-1 block">Code (2-3 lettres)</label>
                <input
                  v-model="customLanguage.code"
                  type="text"
                  placeholder="fr"
                  maxlength="3"
                  class="w-full p-3 bg-slate-50 rounded-xl border text-sm"
                  @input="customLanguage.code = customLanguage.code.toLowerCase()"
                />
              </div>
              <div>
                <label class="text-xs text-slate-500 mb-1 block">Label</label>
                <input
                  v-model="customLanguage.label"
                  type="text"
                  placeholder="Français"
                  class="w-full p-3 bg-slate-50 rounded-xl border text-sm"
                />
              </div>
              <div>
                <label class="text-xs text-slate-500 mb-1 block">Emoji Flag</label>
                <input
                  v-model="customLanguage.flag"
                  type="text"
                  placeholder="🇫🇷"
                  maxlength="4"
                  class="w-full p-3 bg-slate-50 rounded-xl border text-sm text-center text-2xl"
                />
              </div>
            </div>
            <div v-if="validationError" class="text-xs text-red-500">
              {{ validationError }}
            </div>
            <button
              @click="addCustomLanguage"
              class="w-full py-3 bg-indigo-500 text-white rounded-xl font-semibold hover:bg-indigo-600 transition-colors"
            >
              Ajouter la langue personnalisée
            </button>
          </div>
        </div>
      </div>
    </div>
  </Transition>
</template>

<script setup>
import { ref, computed } from 'vue';

const props = defineProps({
  show: {
    type: Boolean,
    default: false
  },
  enabledLanguages: {
    type: Array,
    default: () => []
  }
});

const emit = defineEmits(['close', 'add-language', 'remove-language']);

// Custom language form
const customLanguage = ref({
  code: '',
  label: '',
  flag: ''
});

const validationError = ref('');

// Predefined languages
const predefinedLanguages = [
  { code: 'fr', label: 'Français', flag: '🇫🇷' },
  { code: 'en', label: 'English', flag: '🇬🇧' },
  { code: 'de', label: 'Deutsch', flag: '🇩🇪' },
  { code: 'it', label: 'Italiano', flag: '🇮🇹' },
  { code: 'es', label: 'Español', flag: '🇪🇸' },
  { code: 'pt', label: 'Português', flag: '🇵🇹' },
  { code: 'nl', label: 'Nederlands', flag: '🇳🇱' },
  { code: 'pl', label: 'Polski', flag: '🇵🇱' },
  { code: 'ru', label: 'Русский', flag: '🇷🇺' },
  { code: 'zh', label: '中文', flag: '🇨🇳' },
  { code: 'ja', label: '日本語', flag: '🇯🇵' },
  { code: 'ko', label: '한국어', flag: '🇰🇷' },
  { code: 'ar', label: 'العربية', flag: '🇸🇦' },
  { code: 'tr', label: 'Türkçe', flag: '🇹🇷' },
  { code: 'sv', label: 'Svenska', flag: '🇸🇪' }
];

// Filter out already enabled predefined languages
const availablePredefinedLanguages = computed(() => {
  return predefinedLanguages.filter(
    (lang) => !props.enabledLanguages.some((enabled) => enabled.code === lang.code)
  );
});

const addPredefinedLanguage = (lang) => {
  emit('add-language', {
    code: lang.code,
    label: lang.label,
    flag: lang.flag,
    custom: false,
    order: props.enabledLanguages.length
  });
};

const addCustomLanguage = () => {
  validationError.value = '';

  // Validation
  if (!customLanguage.value.code || customLanguage.value.code.length < 2) {
    validationError.value = 'Le code doit contenir au moins 2 caractères';
    return;
  }

  if (!customLanguage.value.label) {
    validationError.value = 'Le label est requis';
    return;
  }

  // Check if code already exists
  if (props.enabledLanguages.some((lang) => lang.code === customLanguage.value.code)) {
    validationError.value = 'Ce code de langue existe déjà';
    return;
  }

  // Add the custom language
  emit('add-language', {
    code: customLanguage.value.code,
    label: customLanguage.value.label,
    flag: customLanguage.value.flag || '🌐',
    custom: true,
    order: props.enabledLanguages.length
  });

  // Reset form
  customLanguage.value = { code: '', label: '', flag: '' };
};

const removeLanguage = (index) => {
  if (props.enabledLanguages.length === 1) {
    return; // Don't allow removing the last language
  }
  emit('remove-language', index);
};
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.scale-up {
  animation: scale-up 0.2s ease;
}

@keyframes scale-up {
  from {
    transform: scale(0.95);
    opacity: 0;
  }
  to {
    transform: scale(1);
    opacity: 1;
  }
}
</style>
